import React, { useState, useEffect } from 'react';
import agencyService from '../../services/agencyService';
import './ErrorLogs.css';

const ErrorLogs = () => {
  const [errors, setErrors] = useState([]);
  const [agents, setAgents] = useState([]);
  const [filters, setFilters] = useState({
    agentId: '',
    startDate: '',
    endDate: '',
    searchMessage: ''
  });
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 50,
    total: 0
  });
  const [loading, setLoading] = useState(false);
  const [selectedError, setSelectedError] = useState(null);

  useEffect(() => {
    loadAgents();
    loadErrors();
  }, []);

  useEffect(() => {
    loadErrors();
  }, [filters, pagination.page]);

  const loadAgents = async () => {
    try {
      const response = await agencyService.request('/agency/agents?limit=1000');
      if (response.success) {
        setAgents(response.data?.agents || []);
      }
    } catch (error) {
      console.error('Failed to load agents:', error);
    }
  };

  const loadErrors = async () => {
    setLoading(true);
    try {
      const queryParams = new URLSearchParams({
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(Object.entries(filters).filter(([_, v]) => v))
      });

      const response = await agencyService.request(`/admin/error-logs?${queryParams}`);
      if (response.success) {
        const body = response.data || response;
        setErrors(body.errors || []);
        setPagination(prev => ({
          ...prev,
          total: body.total
        }));
      }
    } catch (error) {
      console.error('Failed to load error logs:', error);
    } finally {
      setLoading(false);
    }
  };

  // context_data arrives as an object from the API (or a JSON string in older rows); never let a bad row crash the page
  const parseContext = (value) => {
    try { return typeof value === 'string' ? JSON.parse(value) : value; } catch { return null; }
  };

  const formatTimeline = (contextData) => {
    try {
      const context = parseContext(contextData);
      const actions = context?.recentActions || [];
      
      if (actions.length === 0) return 'No action timeline available';
      
      return actions.map((action, index) => {
        const time = new Date(action.timestamp).toLocaleTimeString();
        let description = '';
        
        switch (action.type) {
          case 'click':
            description = `Clicked ${action.data?.element || 'element'}`;
            if (action.data?.text) {
              description += ` "${action.data.text.substring(0, 30)}"`;
            }
            break;
          case 'input_change':
            description = `Changed ${action.data?.element || 'field'}`;
            if (action.data?.name) {
              description += ` "${action.data.name}"`;
            }
            break;
          case 'form_submit':
            description = `Submitted form`;
            break;
          case 'modal_open':
            description = `Opened modal`;
            break;
          case 'navigation':
            description = `Navigated to ${action.data?.pathname || action.data?.to}`;
            break;
          case 'scroll':
            description = `Scrolled`;
            break;
          default:
            description = action.type;
        }
        
        return `${index + 1}. [${time}] ${description}`;
      }).join('\n');
    } catch (error) {
      return 'Unable to parse action timeline';
    }
  };

  const copyTimeline = (contextData) => {
    const timeline = formatTimeline(contextData);
    navigator.clipboard.writeText(timeline);
    alert('Timeline copied to clipboard!');
  };

  const getAgentName = (agentId) => {
    const agent = agents.find(a => a.id === agentId);
    return agent ? `${agent.first_name} ${agent.last_name}` : `Agent ${agentId}`;
  };

  const getSeverityColor = (message) => {
    if (message?.includes('ReferenceError') || message?.includes('TypeError')) return 'critical';
    if (message?.includes('network') || message?.includes('fetch')) return 'warning';
    return 'info';
  };

  return (
    <div className="error-logs-container">
      <div className="error-logs-header">
        <h1>Frontend Error Logs</h1>
        <p>View and debug frontend JavaScript errors with user action timelines</p>
      </div>

      {/* Filters */}
      <div className="error-logs-filters">
        <div className="filter-group">
          <label>Agent</label>
          <select 
            value={filters.agentId} 
            onChange={(e) => setFilters({...filters, agentId: e.target.value, page: 1})}
          >
            <option value="">All Agents</option>
            {agents.map(agent => (
              <option key={agent.id} value={agent.id}>
                {agent.first_name} {agent.last_name}
              </option>
            ))}
          </select>
        </div>

        <div className="filter-group">
          <label>Start Date</label>
          <input 
            type="date" 
            value={filters.startDate}
            onChange={(e) => setFilters({...filters, startDate: e.target.value, page: 1})}
          />
        </div>

        <div className="filter-group">
          <label>End Date</label>
          <input 
            type="date" 
            value={filters.endDate}
            onChange={(e) => setFilters({...filters, endDate: e.target.value, page: 1})}
          />
        </div>

        <div className="filter-group">
          <label>Search Error</label>
          <input 
            type="text" 
            placeholder="Search error message..."
            value={filters.searchMessage}
            onChange={(e) => setFilters({...filters, searchMessage: e.target.value, page: 1})}
          />
        </div>

        <button 
          className="clear-filters-btn"
          onClick={() => setFilters({ agentId: '', startDate: '', endDate: '', searchMessage: '' })}
        >
          Clear Filters
        </button>
      </div>

      {/* Error List */}
      <div className="error-logs-list">
        {loading ? (
          <div className="loading">Loading errors...</div>
        ) : errors.length === 0 ? (
          <div className="no-errors">No errors found</div>
        ) : (
          errors.map(error => (
            <div 
              key={error.id} 
              className={`error-card ${getSeverityColor(error.message)}`}
              onClick={() => setSelectedError(error)}
            >
              <div className="error-card-header">
                <div className="error-info">
                  <span className="error-agent">{getAgentName(error.agent_id)}</span>
                  <span className="error-time">{new Date(error.created_at).toLocaleString()}</span>
                </div>
                <div className="error-url">{error.page_url}</div>
              </div>
              <div className="error-message">{error.message}</div>
              {error.context_data && (
                <div className="error-actions-preview">
                  📋 {parseContext(error.context_data)?.recentActions?.length || 0} user actions recorded
                </div>
              )}
            </div>
          ))
        )}
      </div>

      {/* Pagination */}
      {pagination.total > pagination.limit && (
        <div className="pagination">
          <button 
            disabled={pagination.page === 1}
            onClick={() => setPagination({...pagination, page: pagination.page - 1})}
          >
            Previous
          </button>
          <span>Page {pagination.page} of {Math.ceil(pagination.total / pagination.limit)}</span>
          <button 
            disabled={pagination.page >= Math.ceil(pagination.total / pagination.limit)}
            onClick={() => setPagination({...pagination, page: pagination.page + 1})}
          >
            Next
          </button>
        </div>
      )}

      {/* Error Detail Modal */}
      {selectedError && (
        <div className="error-modal-overlay" onClick={() => setSelectedError(null)}>
          <div className="error-modal" onClick={(e) => e.stopPropagation()}>
            <div className="error-modal-header">
              <h2>Error Details</h2>
              <button onClick={() => setSelectedError(null)}>×</button>
            </div>

            <div className="error-modal-content">
              <div className="error-detail-section">
                <h3>Agent Information</h3>
                <p><strong>Agent:</strong> {getAgentName(selectedError.agent_id)}</p>
                <p><strong>Time:</strong> {new Date(selectedError.created_at).toLocaleString()}</p>
                <p><strong>Page:</strong> {selectedError.page_url}</p>
                <p><strong>User Agent:</strong> {selectedError.user_agent}</p>
              </div>

              <div className="error-detail-section">
                <h3>Error Message</h3>
                <pre className="error-message-detail">{selectedError.message}</pre>
              </div>

              {selectedError.stack_trace && (
                <div className="error-detail-section">
                  <h3>Stack Trace</h3>
                  <pre className="stack-trace">{selectedError.stack_trace}</pre>
                </div>
              )}

              {selectedError.context_data && (
                <div className="error-detail-section">
                  <h3>User Action Timeline</h3>
                  <div className="timeline-actions">
                    <button 
                      className="copy-timeline-btn"
                      onClick={() => copyTimeline(selectedError.context_data)}
                    >
                      📋 Copy Timeline
                    </button>
                  </div>
                  <pre className="action-timeline">
                    {formatTimeline(selectedError.context_data)}
                  </pre>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default ErrorLogs;
